import { ScaleIcon, Typography } from "@cpj-ai/panel-npm-package";
import { Checkbox } from "@heroui/react";
import { Dispatch, SetStateAction } from "react";

interface IJobPositionTermsAndConditionConfirmProps {
  acceptedTerms: boolean;
  setAcceptedTerms: Dispatch<SetStateAction<boolean>>;
}
const JobPositionTermsAndConditionConfirm = ({
  acceptedTerms,
  setAcceptedTerms,
}: IJobPositionTermsAndConditionConfirmProps) => {
  return (
    <div className="w-full p-4 flex-row-reverse items-center max-w-full overflow-hidden rounded-lg border border-default-200 mt-4">
      <div className="flex gap-2 border-b-1 border-zinc-300 pb-3 mb-3">
        <ScaleIcon className="w-9 h-9" width={36} height={36} stroke="#000" />
        <div className="w-full mt-1.5">
          <Typography className="font-bold">Terms and Conditions for Job Posting</Typography>
          <div className="h-56 overflow-y-auto mt-3 text-sm">
            <h2 className="font-bold mb-2">Terms of Use</h2>

            <p className="mb-4">
              Welcome to Canada Prime Jobs ("CPJ"), a platform designed to connect job seekers with prospective
              employers and provide a range of career development tools, including AI-driven interview practice,
              credential verification services, and post-hire training. By accessing or using the CPJ website, mobile
              application, or any related services (collectively, the "Services"), you agree to be bound by the
              following Terms of Use ("Terms"). If you do not agree with these Terms, do not use our Services.
            </p>

            <div className="mb-4">
              <h3 className="font-bold mb-2">1. Definitions</h3>
              <ul className="list-disc pl-5 space-y-2">
                <li>
                  "User," "You," or "Your" refers to any individual, candidate, or employer accessing or using CPJ's
                  Services.
                </li>
                <li>
                  "Candidate" refers to a job seeker or individual who registers or uses CPJ's Services to find
                  employment or enhance professional skills.
                </li>
                <li>
                  "Employer" refers to any person, company, or entity that posts job listings or otherwise uses CPJ's
                  Services to recruit and hire candidates.
                </li>
                <li>"We," "Us," or "Our" refers to CPJ (Canada Prime Jobs).</li>
              </ul>
            </div>

            <div className="mb-4">
              <h3 className="font-bold mb-2">2. Eligibility and Account Registration</h3>
              <div className="pl-5">
                <p className="font-semibold mb-2">2.1 Eligibility:</p>
                <p className="mb-2">
                  You must be at least the age of majority in your province or territory of residence (or have the
                  consent of a legal guardian) to use the Services. By registering, you represent and warrant that you
                  meet all eligibility requirements.
                </p>

                <p className="font-semibold mb-2">2.2 Account Creation:</p>
                <p>To access certain Services, you may be required to create an account. You agree to:</p>
                <ul className="list-disc pl-5 space-y-2">
                  <li>Provide accurate, current, and complete information during registration.</li>
                  <li>Promptly update any information that becomes inaccurate or outdated.</li>
                </ul>
              </div>
            </div>

            <div className="mb-4">
              <h3 className="font-bold mb-2">3. Description of Services</h3>
              <div className="pl-5">
                <p className="font-semibold mb-2">3.1 Profile Completion and Resume Creation:</p>
                <ul className="list-disc pl-5 space-y-2">
                  <li>If you already have a resume, you may upload it to your CPJ account.</li>
                  <li>
                    If you do not have a resume, you can fill in the required sections and CPJ will generate a
                    professional resume on your behalf.
                  </li>
                  <li>
                    This information is used to match you with job opportunities aligned with your skills and
                    background.
                  </li>
                </ul>
              </div>
            </div>

            <div className="mb-4">
              <h3 className="font-bold mb-2">4. User Responsibilities</h3>
              <div className="pl-5">
                <p className="font-semibold mb-2">4.1 Accuracy of Information:</p>
                <ul className="list-disc pl-5 space-y-2">
                  <li>
                    You are solely responsible for ensuring that all information, documents, and materials you provide
                    are truthful, accurate, and complete.
                  </li>
                </ul>
              </div>
            </div>

            <div className="mb-4">
              <h3 className="font-bold mb-2">5. Privacy and Data Security</h3>
              <div className="pl-5">
                <p className="mb-2">
                  We take your privacy seriously and protect your personal information according to our Privacy Policy
                  and applicable laws.
                </p>
              </div>
            </div>

            <div className="mb-4">
              <h3 className="font-bold mb-2">6. Intellectual Property Rights</h3>
              <div className="pl-5">
                <p className="mb-2">
                  All content on CPJ's platform is protected by copyright and other intellectual property rights.
                </p>
              </div>
            </div>

            <div className="mb-4">
              <h3 className="font-bold mb-2">7. Disclaimers and Limitation of Liability</h3>
              <div className="pl-5">
                <p className="mb-2">
                  CPJ provides services "as is" and makes no warranties about the accuracy or completeness of job
                  listings or user content.
                </p>
              </div>
            </div>

            <div className="mb-4">
              <h3 className="font-bold mb-2">8. Contact Information</h3>
              <p className="mb-2">For questions or concerns about these Terms, please contact us at:</p>
              <ul className="list-disc pl-5 space-y-2">
                <li>Address: 200 Burrard St, Vancouver, Canada</li>
                <li>Phone: +1 (204) 650-2650 or +1 (778) 718-0629</li>
                <li>Email: [Insert official email for legal or general inquiries]</li>
              </ul>
            </div>

            <p className="mb-4">
              By accessing or using CPJ's Services, you acknowledge that you have read, understood, and agreed to be
              bound by these Terms of Use. If you do not agree, please discontinue use of the CPJ platform immediately.
            </p>
          </div>
        </div>
      </div>

      <Checkbox
        classNames={{ label: "text-zinc-500" }}
        color="success"
        isSelected={acceptedTerms}
        onValueChange={setAcceptedTerms}
      >
        I confirm that the new job post are in accordance with the{" "}
        <span className="text-blue-500">rules and conditions</span> of the site
      </Checkbox>
    </div>
  );
};

export default JobPositionTermsAndConditionConfirm;
